import { formatProducesResponse } from './CsProducesResponseScanner';

export const ACTOR_NAMESPACE = '(actor)'; // synthetic namespace key CsSeqCanvasLayoutBuilder checks for to skip the package wrapper and render a stick-figure instead of a header box
export const ACTOR_PARTICIPANT_NAME = 'Caller'; // default label, renamable in place like any other participant

/**
 * Converts walkCallChain() output into a flat sequence-diagram-ready shape:
 * boxes (grouped by namespace, mirrors mermaid's `box` grouping) and an
 * ordered list of messages \u2014 CALLS interleaved with their matching RETURNS
 * (see buildEventRows). Not a UmlShape/canvas type \u2014 sequence diagrams
 * render separately (mermaid sequenceDiagram AND the canvas, see
 * CsSeqCanvasLayoutBuilder), not directly on this shape.
 *
 * Prepends a synthetic "found message" (standard UML term for a call whose
 * origin is outside the modeled system) from an external ACTOR into the
 * entry class/method \u2014 without it, the entry method's own name never shows
 * up anywhere on the diagram (its first REAL step is a call it makes to
 * someone else, not a call INTO it), which reads like the call started out
 * of nowhere. depth -1 (shallower than every real step, which start at 0)
 * makes it close/return LAST, via the same generic stack logic in
 * buildEventRows \u2014 no special-casing needed there.
 *
 * Participant order within a box is plain FIRST-APPEARANCE (Array.from a
 * Set built in call order) \u2014 deliberately NOT reordered to minimize
 * crossings: every call must go left-to-right and every return
 * right-to-left, since a participant is only ever introduced by being
 * CALLED, always placed after (to the right of) its caller. Any reordering
 * that puts a callee to the LEFT of its caller (e.g. to reduce line
 * crossings) breaks that invariant and produces a return arrow pointing the
 * wrong way.
 * @param {{steps: object[], warnings: string[], entryNamespace: string|null, entryReturnType: string|undefined}} chain - output of walkCallChain
 * @param {string} entryClassName
 * @param {string} entryMethodName
 * @param {{typeName: string, statusCode: string}[]} [producesResponses] - [ProducesResponseType(...)] attributes found directly on the entry method (see CsProducesResponseScanner) — when present, these REPLACE the entry's own return-type label, one line per attribute (multi-line label, same top-to-bottom order they're declared), since that's what an ASP.NET controller action's actual documented responses are — the C# return type alone (e.g. `Task<IActionResult>`) says nothing useful there.
 * @returns {{boxes: {namespace: string, participants: string[]}[], messages: {from: string, to: string, method: string, boundary: boolean, isReturn: boolean, stepRef: object}[], warnings: string[]}}
 */
export function buildSequenceShape(chain, entryClassName, entryMethodName, producesResponses = []) {
  const entryStep = {
    fromClass: ACTOR_PARTICIPANT_NAME,
    fromNamespace: ACTOR_NAMESPACE,
    toClass: entryClassName,
    toNamespace: chain.entryNamespace,
    method: entryMethodName,
    boundary: false,
    depth: -1,
    returnType: chain.entryReturnType,
    params: chain.entryParams,
    producesResponseLabel: producesResponses.length > 0
      ? producesResponses.map(formatProducesResponse).join('\n')
      : null,
  };
  const steps = [entryStep, ...chain.steps];

  const boxByNamespace = new Map();

  function ensureParticipant(namespace, className) {
    const key = namespace ?? '(unknown)';
    if (!boxByNamespace.has(key)) {
      boxByNamespace.set(key, new Set());
    }
    boxByNamespace.get(key).add(className);
  }

  steps.forEach(function collectParticipants(step) {
    ensureParticipant(step.fromNamespace, step.fromClass);
    ensureParticipant(step.toNamespace, step.toClass);
  });

  const messages = buildEventRows(steps).map(function toMessage(event) {
    if (event.type === 'call') {
      return { from: event.step.fromClass, to: event.step.toClass, method: event.step.method, boundary: event.step.boundary, isReturn: false, stepRef: event.step, thirdLevel: Boolean(event.step.thirdLevel), params: event.step.params };
    }
    // Entry method's own return: use the ProducesResponseType-derived multi-line
    // label if we have one, otherwise fall back to the plain C# return type.
    if (event.step.producesResponseLabel) {
      return { from: event.step.toClass, to: event.step.fromClass, method: event.step.producesResponseLabel, boundary: false, isReturn: true, stepRef: event.step, thirdLevel: Boolean(event.step.thirdLevel) };
    }
    const hasValue = event.step.returnType && event.step.returnType !== 'void';
    return { from: event.step.toClass, to: event.step.fromClass, method: hasValue ? event.step.returnType : '', boundary: false, isReturn: true, stepRef: event.step, thirdLevel: Boolean(event.step.thirdLevel) };
  });

  const boxes = Array.from(boxByNamespace.entries()).map(function toBox([namespace, participants]) {
    return { namespace, participants: Array.from(participants) };
  });

  return { boxes, messages, warnings: chain.warnings };
}

/**
 * Turns the flat, preorder `steps` list (walkCallChain is a DFS \u2014 a call's
 * own downstream calls all appear right after it, before its next sibling)
 * into an interleaved CALL/RETURN event list using a depth-tracking stack:
 * whenever the next step's depth is <= the depth of whatever's on top of the
 * stack, that means everything deeper has finished, so emit a 'return' for
 * it before continuing. Boundary steps (unresolved type, or an event
 * pub/sub we deliberately don't follow) get NO return event \u2014 we have no
 * data on what happens past that boundary, only the call itself is known.
 *
 * Each 'return' event's `step` is the SAME object reference as its matching
 * 'call' event's `step` \u2014 used downstream (CsSeqCanvasLayoutBuilder's
 * activation-range pairing) to match a return back to its own call by
 * identity instead of re-deriving the relationship.
 * @param {object[]} steps - chain.steps (plus the synthetic entry step), each with a `depth`
 * @returns {{type: 'call'|'return', step: object}[]}
 */
function buildEventRows(steps) {
  const events = [];
  const stack = [];

  function closeStackDownTo(depth) {
    while (stack.length > 0 && stack[stack.length - 1].depth >= depth) {
      const closed = stack.pop();
      if (!closed.boundary) {
        events.push({ type: 'return', step: closed });
      }
    }
  }

  steps.forEach(function placeStep(step) {
    closeStackDownTo(step.depth);
    events.push({ type: 'call', step });
    stack.push(step);
  });
  closeStackDownTo(-Infinity); // close everything still open at the end

  return events;
}
